import { useCallback, useEffect, useRef, useState } from 'react';
import { useNetworkStatus } from '@/presentation/hooks/useNetworkStatus';
import { SyncLeadQueueUseCase } from '@/application/use-cases/SyncLeadQueueUseCase';
import { IndexedDbLeadQueueRepository } from '@/infrastructure/repositories/IndexedDbLeadQueueRepository';
import { IndexedDbSyncBookkeepingRepository } from '@/infrastructure/repositories/IndexedDbSyncBookkeepingRepository';
import { IndexedDbActiveSessionRepository } from '@/infrastructure/repositories/IndexedDbActiveSessionRepository';
import { HttpLeadRepository } from '@/infrastructure/repositories/HttpLeadRepository';
import { isBackgroundSyncSupported } from '@/infrastructure/sync/backgroundSyncSupport';
import { scheduleLeadSync } from '@/infrastructure/sync/scheduleLeadSync';

/** CA-06: intervalo do fallback de polling quando Background Sync não é
 * suportado (iOS Safari, navegadores legados). */
const POLLING_INTERVAL_MS = 30_000;

const leadQueueRepository = new IndexedDbLeadQueueRepository();
const syncLeadQueueUseCase = new SyncLeadQueueUseCase({
  leadQueueRepository,
  syncBookkeepingRepository: new IndexedDbSyncBookkeepingRepository(),
  activeSessionRepository: new IndexedDbActiveSessionRepository(),
  leadRepository: new HttpLeadRepository(),
});

export type LeadQueueIndicatorState = 'online' | 'offline' | 'syncing' | 'stuck';

export interface UseLeadQueueSyncResult {
  /** Estado do `OnlineStatusIndicator` (CA-07): derivado de conectividade +
   * fila pendente — "syncing" cobre tanto Background Sync quanto o
   * fallback de polling, já que ambos mantêm a fila drenando enquanto
   * online (não há como o main thread observar diretamente uma
   * sincronização feita pelo Service Worker sem mensageria adicional; a
   * combinação online + fila não-vazia já é, na prática, equivalente a
   * "sincronização em andamento" neste desenho). "stuck" (LOCAL-002) tem
   * prioridade sobre os demais: sinaliza que ao menos um lead pendente tem
   * falha permanente (409/422, `isPermanentSyncFailure`) — um problema de
   * dados que não se resolve reconectando, então vale mais para o operador
   * do que o estado de conectividade no momento. */
  indicatorState: LeadQueueIndicatorState;
  pendingCount: number;
  /** Chamado após um lead ser enfileirado com sucesso (CA-04): agenda a
   * sincronização via Background Sync quando suportado, ou garante que o
   * fallback de polling está ativo. */
  notifyLeadEnqueued: () => void;
}

/**
 * Orquestra a sincronização da fila de leads no main thread: aciona o
 * Background Sync quando suportado (CA-04/CA-05) ou o fallback de polling
 * de 30s (CA-06), e deriva o estado do indicador visual (CA-07) a partir da
 * contagem de leads pendentes + conectividade.
 */
export function useLeadQueueSync(): UseLeadQueueSyncResult {
  const online = useNetworkStatus();
  const [pendingCount, setPendingCount] = useState(0);
  const [hasPermanentFailure, setHasPermanentFailure] = useState(false);
  const syncInFlightRef = useRef(false);

  const refreshPendingCount = useCallback(async (): Promise<void> => {
    try {
      const count = await leadQueueRepository.count();
      setPendingCount(count);
    } catch (error) {
      // Leitura de contagem falhou (IndexedDB indisponível — CA-03): mantém
      // o último valor conhecido em vez de travar o indicador; não é um
      // erro exposto em UI.
      console.error('[useLeadQueueSync] Falha ao consultar o total da fila.', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }, []);

  // LOCAL-002: mesma disciplina de `refreshPendingCount` (mantém o último
  // valor conhecido em falha, log estruturado, sem exposição em UI) — função
  // separada em vez de combinar as duas leituras num só `Promise.all` para
  // não acoplar o tratamento de erro de uma consulta à outra.
  const refreshHasPermanentFailure = useCallback(async (): Promise<void> => {
    try {
      const stuck = await leadQueueRepository.hasPermanentFailure();
      setHasPermanentFailure(stuck);
    } catch (error) {
      console.error('[useLeadQueueSync] Falha ao consultar falhas permanentes da fila.', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }, []);

  const refreshQueueState = useCallback(async (): Promise<void> => {
    await Promise.all([refreshPendingCount(), refreshHasPermanentFailure()]);
  }, [refreshPendingCount, refreshHasPermanentFailure]);

  const runFallbackSync = useCallback(async (): Promise<void> => {
    if (syncInFlightRef.current) return;
    syncInFlightRef.current = true;
    try {
      await syncLeadQueueUseCase.execute();
    } finally {
      syncInFlightRef.current = false;
      await refreshQueueState();
    }
  }, [refreshQueueState]);

  const triggerSync = useCallback((): void => {
    if (isBackgroundSyncSupported()) {
      void scheduleLeadSync();
      return;
    }
    void runFallbackSync();
  }, [runFallbackSync]);

  const notifyLeadEnqueued = useCallback((): void => {
    void refreshQueueState();
    triggerSync();
  }, [refreshQueueState, triggerSync]);

  // Contagem inicial ao montar — cobre CA-10 (app remontada com fila já
  // persistida de uma sessão anterior), incluindo eventuais leads com falha
  // permanente já registrada antes do refresh (LOCAL-002).
  useEffect(() => {
    void refreshQueueState();
  }, [refreshQueueState]);

  // CA-04/CA-06: dispara a sincronização assim que o dispositivo está
  // online — tanto na reconexão quanto já no primeiro render se a conexão
  // já estava presente (ex.: fila residual de uma sessão anterior).
  useEffect(() => {
    if (online) triggerSync();
  }, [online, triggerSync]);

  // CA-06: fallback de polling. Enquanto Background Sync não é suportado e
  // há leads pendentes com o dispositivo online, repete a cada 30s; o
  // efeito re-executa a cada mudança de `online`/`pendingCount` e a limpeza
  // cancela o intervalo anterior — o timer "para quando a fila esvaziar ou
  // a conexão cair" por construção (a condição do efeito deixa de valer).
  useEffect(() => {
    if (isBackgroundSyncSupported() || !online || pendingCount === 0) return undefined;

    const intervalId = setInterval(() => {
      void runFallbackSync();
    }, POLLING_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [online, pendingCount, runFallbackSync]);

  const indicatorState: LeadQueueIndicatorState = hasPermanentFailure
    ? 'stuck'
    : !online
      ? 'offline'
      : pendingCount > 0
        ? 'syncing'
        : 'online';

  return { indicatorState, pendingCount, notifyLeadEnqueued };
}
